import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import VersoLogo from './VersoLogo';

const Loading = ({
  message = 'Verso está trabalhando...',
  variant = 'circular', // circular, skeleton, card, minimal
  size = 'medium', // small, medium, large
  fullHeight = true,
  showIcon = false,
  color = 'primary',
}) => {
  const theme = useTheme();

  // Tamanhos baseados na prop size
  const sizes = {
    small: { progress: 24, container: 120 },
    medium: { progress: 40, container: 200 },
    large: { progress: 60, container: 300 },
  };

  const currentSize = sizes[size];

  // Loading circular padrão modernizado
  if (variant === 'circular') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height={fullHeight ? '100%' : 'auto'}
        minHeight={fullHeight ? currentSize.container : 'auto'}
        sx={{
          padding: 3,
          position: 'relative',
        }}
      >
        {/* Background com efeito glassmorphism */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
          }}
        />

        {/* Conteúdo */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <VersoLogo size={currentSize.progress} animated={true} />

          {/* Mensagem */}
          {message && (
            <Box textAlign="center" sx={{ maxWidth: 300 }}>
              <Typography
                variant={size === 'small' ? 'body2' : 'body1'}
                color="text.primary"
                fontWeight={500}
                gutterBottom
              >
                {message}
              </Typography>

              {/* Pontos animados */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  '&::after': {
                    content: '"..."',
                    display: 'inline-block',
                    animation: 'dots 1.5s infinite',
                    width: '20px',
                    textAlign: 'left',
                  },
                  '@keyframes dots': {
                    '0%, 20%': { content: '""' },
                    '40%': { content: '"."' },
                    '60%': { content: '".."' },
                    '80%, 100%': { content: '"..."' },
                  },
                }}
              >
                Aguarde
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Loading skeleton para listas
  if (variant === 'skeleton') {
    return (
      <Box sx={{ width: '100%' }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card
            key={index}
            sx={{
              mb: 2,
              animationDelay: `${index * 0.1}s`,
              animation: 'fadeIn 0.6s ease-out both',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Skeleton variant="circular" width={48} height={48} />
                <Box flexGrow={1}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
                <Skeleton variant="rectangular" width={80} height={32} />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Loading card para formulários
  if (variant === 'card') {
    return (
      <Card
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          animation: 'fadeIn 0.6s ease-out',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={3}
          >
            <VersoLogo size={80} animated={true} />

            <Box textAlign="center">
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Isso pode levar alguns segundos...
              </Typography>
            </Box>

            {/* Barra de progresso fictícia */}
            <Box sx={{ width: '100%', maxWidth: 300 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 6,
                  backgroundColor: alpha(theme.palette[color].main, 0.1),
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
                    borderRadius: 3,
                    animation: 'shimmer 2s infinite',
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Loading minimal para botões ou espaços pequenos
  if (variant === 'minimal') {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        sx={{ py: 1 }}
      >
        <CircularProgress color={color} size={20} thickness={4} aria-label="Carregando" />
        {message && (
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  // Fallback para variant não reconhecida
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height={fullHeight ? '100%' : 'auto'}
      minHeight={fullHeight ? currentSize.container : 'auto'}
    >
      <CircularProgress color={color} size={currentSize.progress} aria-label="Carregando conteúdo" />
      {message && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default Loading;
